import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSetActiveOrganization } from '@repo/api-client/react';
import { useCreateOrganization, useUploadOrgLogo } from '@repo/queries';
import { Page, PageHeader } from '@/features/common/PageHeader';
import { BusinessForm } from '@/features/org/BusinessForm';
import { businessFormToCreate, type BusinessFormValues } from '@/features/org/businessFormSchema';
import { useToast } from '@/components/ui/use-toast';
import { describeAppError } from '@/features/common/describeAppError';
import { BusinessAvatar, StagedImagePicker } from '@/features/avatar';

/**
 * Final onboarding step: create the user's first business and drop
 * them into the dashboard. The form is the same as the standalone
 * `BusinessCreate` screen — same schema, same defaults — so once
 * the user has done it once they recognise it the next time.
 */
export function OnboardingCreateBusinessScreen() {
  const navigate = useNavigate();
  const { createOrganization, loading } = useCreateOrganization();
  const { uploadLogo } = useUploadOrgLogo();
  const setActive = useSetActiveOrganization();
  const { toast } = useToast();

  // Same stage-then-upload pattern as BusinessCreate; see comments
  // there for why the logo isn't part of the form schema.
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [stagedName, setStagedName] = useState<string>('');

  async function handleSubmit(values: BusinessFormValues) {
    try {
      const org = await createOrganization(businessFormToCreate(values));
      setActive(org.id);
      if (logoFile) {
        try {
          await uploadLogo({ organizationId: org.id, file: logoFile });
        } catch (err) {
          const desc = describeAppError(err);
          toast({
            title: 'Logo upload failed',
            description: desc.message,
            variant: 'danger',
          });
        }
      }
      toast({ title: 'Welcome aboard!', variant: 'success' });
      navigate('/');
    } catch (err) {
      const desc = describeAppError(err);
      toast({
        title: desc.title,
        description: desc.message,
        variant: 'danger',
      });
    }
  }

  return (
    <Page>
      <PageHeader
        title="Create your first business"
        description="Tell us about your studio so we can tailor invoice defaults. You can change any of this later in Settings."
      />
      <BusinessForm
        loading={loading}
        submitLabel="Create business and continue"
        onSubmit={(values) => {
          setStagedName(values.name ?? '');
          return handleSubmit(values);
        }}
        logoSlot={
          <StagedImagePicker
            title="Logo"
            hint="Square images render best. PNG, JPG or WebP up to 5 MB."
            file={logoFile}
            onChange={setLogoFile}
            renderPreview={(previewUrl) => (
              <BusinessAvatar
                id="pending"
                name={stagedName || 'Your business'}
                logoUrl={previewUrl}
                size={64}
              />
            )}
          />
        }
      />
    </Page>
  );
}
